import React, { useState } from "react";
import { Button, InputNumber, Space, Tooltip, message } from "antd";
import { SyncOutlined, UndoOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";

import * as adminactions from "store/actions/AuthActions";

/**
 * Exchange rate field for quotes and invoices, with two icon actions on the
 * right: fetch today's rate for `currencyIso` -> INR, and put back the rate the
 * document was saved with.
 *
 * Used as the direct child of a Form.Item, which supplies `value`/`onChange`.
 * `savedRate` is the rate stored on the document; leave it out for a new one
 * (or when the currency was changed away from the saved one) and the reset
 * icon stays disabled.
 */
const ExchangeRateInput = ({ value, onChange, currencyIso, savedRate }) => {
  const [fetching, setFetching] = useState(false);
  const organization = useSelector((state) => state.auth.organization);

  const hasSavedRate = savedRate !== undefined && savedRate !== null && savedRate !== "";
  const isSavedRate = hasSavedRate && Number(value) === Number(savedRate);

  const fetchLatest = async () => {
    if (!currencyIso) {
      return;
    }
    setFetching(true);
    const data = await adminactions.fetchExchangeRate(
      currencyIso,
      "INR",
      process.env.REACT_APP_ORGANIZATION_UUID || organization
    );
    setFetching(false);
    if (data?.success && data.rate) {
      onChange?.(Number(Number(data.rate).toFixed(6)));
      message.success(
        `1 ${currencyIso} = ₹${data.rate}` +
          (data.source_label ? ` · ${data.source_label}` : "") +
          (data.date ? ` (as of ${data.date})` : "")
      );
      if (data.note) {
        message.info(data.note);
      }
    } else {
      message.error(data?.error || "Could not fetch the latest exchange rate");
    }
  };

  const resetRate = () => {
    if (hasSavedRate) {
      onChange?.(Number(savedRate));
    }
  };

  return (
    <Space.Compact style={{ width: "100%" }}>
      <InputNumber
        value={value}
        onChange={onChange}
        addonBefore={currencyIso ? `1 ${currencyIso} = ₹` : "₹"}
        min={0.000001}
        step={0.01}
        style={{ width: "100%" }}
        placeholder="e.g. 83.50"
      />
      <Tooltip title="Fetch latest rate (source is set in Settings → Accounting)">
        <Button
          icon={<SyncOutlined spin={fetching} />}
          onClick={fetchLatest}
          disabled={fetching || !currencyIso}
          aria-label="Fetch latest rate"
        />
      </Tooltip>
      <Tooltip title={hasSavedRate ? `Reset to saved rate (${Number(savedRate)})` : "No saved rate yet"}>
        <Button
          icon={<UndoOutlined />}
          onClick={resetRate}
          disabled={!hasSavedRate || isSavedRate}
          aria-label="Reset to saved rate"
        />
      </Tooltip>
    </Space.Compact>
  );
};

export default ExchangeRateInput;
